import { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import ProductCard from '../components/ProductCard';
import { Search, ArrowRight, Leaf, Truck, Sun, ShieldCheck, Star, Quote, MapPin, Sparkles } from 'lucide-react';

const ICONS = { leaf: Leaf, sun: Sun, truck: Truck, 'shield-check': ShieldCheck, sparkles: Sparkles };

export default function HomePage() {
  const [categories, setCategories] = useState([]);
  const [farmers, setFarmers] = useState([]);
  const [products, setProducts] = useState([]);
  const [cms, setCms] = useState(null);
  const [hero, setHero] = useState({ images: [], interval_ms: 4000, auto_rotate: true });
  const [trustTags, setTrustTags] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [heroIdx, setHeroIdx] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    Promise.all([
      api.get('/categories').then(r => setCategories(r.data || [])).catch(() => {}),
      api.get('/farmers').then(r => setFarmers(r.data || [])).catch(() => {}),
      api.get('/products').then(r => setProducts(r.data || [])).catch(() => {}),
      api.get('/settings/homepage').then(r => setCms(r.data || {})).catch(() => {}),
      api.get('/settings/homepage_hero').then(r => setHero({ ...hero, ...(r.data || {}) })).catch(() => {}),
      api.get('/settings/homepage_trust_tags').then(r => setTrustTags(r.data || [])).catch(() => {}),
      api.get('/reviews/top?limit=5').then(r => setReviews(r.data || [])).catch(() => {}),
      api.get('/faq/general').then(r => setFaqs(r.data || [])).catch(() => {}),
    ]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Hero auto-rotate
  const heroImages = useMemo(() => {
    if (hero.images?.length) return hero.images;
    return [
      cms?.hero_image || 'https://images.unsplash.com/photo-1754810940905-19a8d26f870e?q=80&w=2400&auto=format&fit=crop',
      'https://images.unsplash.com/photo-1721661520500-2cb97d8dcfaa?q=80&w=2400&auto=format&fit=crop',
      'https://images.unsplash.com/photo-1593032465171-24b15ce1c0fb?q=80&w=2400&auto=format&fit=crop',
    ];
  }, [hero.images, cms]);

  useEffect(() => {
    if (!hero.auto_rotate || heroImages.length < 2) return;
    const t = setInterval(() => setHeroIdx(i => (i + 1) % heroImages.length), hero.interval_ms || 4000);
    return () => clearInterval(t);
  }, [hero.auto_rotate, hero.interval_ms, heroImages.length]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) navigate(`/products?search=${encodeURIComponent(searchQuery.trim())}`);
  };

  const trust = trustTags.length > 0 ? trustTags : [
    { label: 'Direct from Farm', icon: 'leaf' },
    { label: 'Organically Grown', icon: 'sun' },
    { label: 'Seasonal Produce', icon: 'truck' },
    { label: 'Lab Tested', icon: 'shield-check' },
  ];

  // Group products by category
  const productsByCategory = useMemo(() => {
    const map = {};
    for (const p of products) {
      const k = p.category || 'other';
      if (!map[k]) map[k] = [];
      map[k].push(p);
    }
    return map;
  }, [products]);

  return (
    <div data-testid="home-page">
      {/* Section 1 - Hero with auto-rotate */}
      <section data-testid="hero-section" className="relative h-[75vh] min-h-[520px] flex items-center justify-center overflow-hidden">
        {heroImages.map((img, i) => (
          <img
            key={i} src={img} alt=""
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${i === heroIdx ? 'opacity-100' : 'opacity-0'}`}
          />
        ))}
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/60" />
        <div className="relative z-10 text-center px-4 max-w-3xl">
          <p className="text-sm font-semibold uppercase tracking-[0.25em] text-white/80 mb-4">Farm Fresh, Always</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-medium text-white tracking-tight mb-6 drop-shadow">
            {cms?.hero_headline || 'From the Soil, Straight to Your Table'}
          </h1>
          <p className="text-base sm:text-lg text-white/85 mb-8 max-w-xl mx-auto">
            {cms?.hero_subheadline || 'Discover seasonal, organic produce directly from trusted farmers.'}
          </p>
          <form onSubmit={handleSearch} className="flex items-center max-w-md mx-auto">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
              <input data-testid="hero-search-input" type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search for mangoes, milk, rice..." className="w-full h-12 pl-12 pr-4 rounded-l-full bg-white text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/20" />
            </div>
            <Button data-testid="hero-search-button" type="submit" className="h-12 px-6 rounded-r-full">Search</Button>
          </form>
          {/* Hero dots */}
          {heroImages.length > 1 && (
            <div className="flex justify-center gap-1.5 mt-8">
              {heroImages.map((_, i) => (
                <button key={i} onClick={() => setHeroIdx(i)}
                  className={`h-1.5 rounded-full transition-all ${i === heroIdx ? 'w-6 bg-white' : 'w-1.5 bg-white/50'}`} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Section 2 - Trust Tags strip */}
      <section data-testid="trust-strip" className="border-b border-border/50 bg-secondary/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 grid grid-cols-2 md:grid-cols-4 gap-4">
          {trust.map((t, i) => {
            const Icon = ICONS[t.icon] || Leaf;
            return (
              <div key={i} data-testid={`trust-tag-${i}`} className="flex items-center gap-2.5 justify-center">
                <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-background shadow-sm"><Icon className="h-4 w-4 text-primary" /></span>
                <span className="text-sm font-medium">{t.label}</span>
              </div>
            );
          })}
        </div>
      </section>

      {/* Section 3 - Browse by Categories */}
      <section data-testid="categories-section" className="py-14 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        <div className="flex items-end justify-between mb-8">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">Browse by</p>
            <h2 className="text-2xl sm:text-3xl font-serif font-medium">Categories</h2>
          </div>
        </div>
        <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-3 sm:gap-4">
          {categories.map((cat) => (
            <Link key={cat.slug} to={`/category/${cat.slug}`} data-testid={`category-card-${cat.slug}`}
              className="group relative overflow-hidden rounded-3xl aspect-square">
              <img src={cat.image} alt={cat.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-3 text-center">
                <h3 className="text-white font-medium text-xs sm:text-sm">{cat.name}</h3>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Section 4 - Seasonal marquee banner */}
      {cms?.seasonal_banner_enabled && cms?.seasonal_banner_text && (
        <section data-testid="seasonal-marquee" className="bg-primary text-primary-foreground py-3 overflow-hidden">
          <div className="whitespace-nowrap animate-marquee text-sm font-medium">
            {Array.from({ length: 8 }).map((_, i) => (
              <span key={i} className="mx-10 inline-flex items-center gap-2">
                <Sparkles className="h-3.5 w-3.5" /> {cms.seasonal_banner_text}
              </span>
            ))}
          </div>
        </section>
      )}

      {/* Section 5 - Category-wise product carousels */}
      {categories.slice(0, 6).map((cat) => {
        const catProducts = productsByCategory[cat.slug] || [];
        if (catProducts.length === 0) return null;
        return (
          <section key={cat.slug} data-testid={`category-section-${cat.slug}`} className="py-10 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
            <div className="flex items-end justify-between mb-5">
              <div className="flex items-center gap-3">
                <span className="text-2xl">{cat.emoji || '🥬'}</span>
                <h2 className="text-xl sm:text-2xl font-serif font-medium">{cat.name}</h2>
              </div>
              <Link to={`/category/${cat.slug}`} className="text-sm font-medium text-primary hover:underline flex items-center gap-1">
                View All <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </div>
            <div className="flex gap-4 overflow-x-auto pb-3 -mx-1 px-1 snap-x scroll-smooth scrollbar-hide">
              {catProducts.slice(0, 12).map((p) => (
                <div key={p.product_id} className="min-w-[220px] sm:min-w-[240px] snap-start">
                  <ProductCard product={p} />
                </div>
              ))}
            </div>
          </section>
        );
      })}

      {/* Section 6 - Peek Into Our Farms (auto-rotate single card every 3s) */}
      {farmers.length > 0 && (
        <FarmersPeek farmers={farmers} />
      )}

      {/* Section 7 - Customer Reviews */}
      {reviews.length > 0 && (
        <section data-testid="reviews-section" className="py-14 px-4 sm:px-6 lg:px-8 bg-secondary/30">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-10">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">From our community</p>
              <h2 className="text-2xl sm:text-3xl font-serif font-medium">What Customers Are Saying</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {reviews.slice(0, 3).map((r) => (
                <div key={r.id} data-testid={`review-${r.id}`} className="bg-card rounded-3xl border border-border/50 p-6 shadow-sm">
                  <Quote className="h-6 w-6 text-primary/30 mb-3" />
                  <div className="flex items-center gap-0.5 mb-3">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Star key={i} className={`h-3.5 w-3.5 ${i < r.rating ? 'text-amber-500 fill-amber-500' : 'text-muted-foreground/30'}`} />
                    ))}
                  </div>
                  <p className="text-sm text-foreground/90 leading-relaxed mb-4 line-clamp-4">{r.review_text}</p>
                  <div className="flex items-center gap-3 pt-3 border-t border-border/50">
                    <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center">
                      <span className="text-xs font-semibold text-primary">{r.customer_name?.[0] || 'C'}</span>
                    </div>
                    <div>
                      <p className="text-sm font-medium">{r.customer_name}</p>
                      <p className="text-xs text-muted-foreground">Verified Purchase</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Section 8 - FAQ */}
      {faqs.length > 0 && (
        <section data-testid="homepage-faq-section" className="py-14 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">FAQ</p>
            <h2 className="text-2xl sm:text-3xl font-serif font-medium">Your Questions, Answered</h2>
          </div>
          <Accordion type="single" collapsible className="space-y-2">
            {faqs.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} data-testid={`faq-item-${i}`} className="bg-card rounded-2xl border border-border/50 px-5">
                <AccordionTrigger className="text-sm font-medium text-left hover:no-underline">{f.question || f.q}</AccordionTrigger>
                <AccordionContent className="text-sm text-muted-foreground whitespace-pre-wrap pb-5">{f.answer || f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>
      )}
    </div>
  );
}

function FarmersPeek({ farmers }) {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    if (farmers.length < 2) return;
    const t = setInterval(() => setIdx(i => (i + 1) % farmers.length), 3000);
    return () => clearInterval(t);
  }, [farmers.length]);
  const f = farmers[idx];
  if (!f) return null;

  return (
    <section data-testid="farmers-peek" className="py-12 px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto">
      <div className="text-center mb-6">
        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">Meet our</p>
        <h2 className="text-2xl sm:text-3xl font-serif font-medium">Peek Into Our Farms</h2>
      </div>
      <div className="relative bg-card rounded-3xl border border-border/50 overflow-hidden shadow-sm">
        <div className="grid grid-cols-1 md:grid-cols-[220px_1fr] gap-0">
          {/* Profile picture column */}
          <div className="relative aspect-square md:aspect-auto">
            <img
              key={f.farmer_id}
              src={f.profile_image || f.cover_image}
              alt={f.name}
              data-testid={`farmer-peek-image-${f.farmer_id}`}
              className="w-full h-full object-cover transition-opacity duration-700 animate-fade-in"
            />
          </div>
          {/* Details column */}
          <div className="p-6 sm:p-8 flex flex-col justify-center">
            <div className="flex items-center gap-2 mb-2">
              <ShieldCheck className="h-4 w-4 text-primary" />
              <span className="text-[11px] font-medium text-primary uppercase tracking-wider">Verified Farmer</span>
            </div>
            <h3 data-testid={`farmer-peek-name-${f.farmer_id}`} className="text-2xl font-serif font-medium mb-1">{f.name}</h3>
            <p className="text-xs text-muted-foreground flex items-center gap-1 mb-3">
              <MapPin className="h-3 w-3" /> {f.farm_name} &middot; {f.location}
            </p>
            <p className="text-sm text-muted-foreground leading-relaxed mb-5 line-clamp-3">{f.story}</p>
            <div>
              <Link to={`/farmer/${f.farmer_id}`}>
                <Button data-testid={`view-farmer-profile-btn-${f.farmer_id}`} className="rounded-full" size="sm">
                  View Profile <ArrowRight className="h-3.5 w-3.5 ml-1.5" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
        {/* Dots */}
        {farmers.length > 1 && (
          <div className="flex justify-center gap-1.5 pb-4">
            {farmers.map((_, i) => (
              <button key={i} onClick={() => setIdx(i)}
                data-testid={`farmer-peek-dot-${i}`}
                className={`h-1.5 rounded-full transition-all ${i === idx ? 'w-6 bg-primary' : 'w-1.5 bg-muted-foreground/30 hover:bg-muted-foreground/50'}`} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
